import type React from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { AriaLiveMessage } from '@xing-com/crate-jobs-components-aria-live-message';
import { mediaConfined } from '@xing-com/layout-tokens';
import { spaceXS, spaceXL, spaceXXL } from '@xing-com/tokens';

import { WizardActions } from './components/wizard-actions';
import { WizardProgressBar } from './components/wizard-progress-bar';
import { WizardReview } from './components/wizard-review';
import type { Step, ExternalDocsLegacySupportProp } from './types';

const WizardContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  gap: ${spaceXL};

  @media ${mediaConfined} {
    gap: ${spaceXXL};
  }
`;

const StepContainer = styled.div`
  flex-grow: 1;
  padding: 0 ${spaceXS};

  @media ${mediaConfined} {
    flex-grow: 0;
  }
`;

type Props = {
  steps: Step[];
  onSubmit: () => void;
  trackingProps: {
    itemUrn: string;
    flags: string;
    page: string;
    context: string;
  };
  externalDocuments?: ExternalDocsLegacySupportProp;
  validate?: (stepIndex: number) => Promise<boolean>;
  isLoading?: boolean;
};

export const Wizard: React.FC<Props> = ({
  steps,
  onSubmit,
  validate,
  isLoading = false,
  externalDocuments,
  trackingProps,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isReviewMode, setIsReviewMode] = useState(false);

  const totalSteps = steps.length + 1; // +1 to account for the review step
  const hasNext = currentStep < totalSteps;
  const hasPrevious = currentStep > 1;
  const isReviewStep = currentStep === totalSteps;

  useEffect(() => {
    if (isReviewStep) setIsReviewMode(isReviewStep);
  }, [isReviewStep]);

  const goToNextStep = async (): Promise<void> => {
    const isValid = await validate?.(currentStep);
    if (hasNext && isValid) setCurrentStep((step) => step + 1);
  };

  const goToPreviousStep = (): void => {
    if (hasPrevious) setCurrentStep((step) => step - 1);
  };

  const goToReviewStep = async (): Promise<void> => {
    const isValid = await validate?.(currentStep);
    if (isValid) setCurrentStep(totalSteps);
  };

  if (isReviewStep) {
    return (
      <WizardReview
        steps={steps}
        isLoading={isLoading}
        onReviewStep={(step) => setCurrentStep(step)}
        onSubmit={onSubmit}
        externalDocuments={externalDocuments}
        trackingProps={trackingProps}
      />
    );
  }

  return (
    <WizardContainer>
      <AriaLiveMessage messageId={steps[currentStep - 1].name} />
      <WizardProgressBar
        currentStep={currentStep}
        totalSteps={steps.length}
        isReviewMode={isReviewMode}
      />
      <StepContainer>{steps[currentStep - 1].content}</StepContainer>
      <WizardActions
        hasPrevious={hasPrevious}
        isReviewMode={isReviewMode}
        isLoading={isLoading}
        onNext={goToNextStep}
        onBack={goToPreviousStep}
        onReview={goToReviewStep}
      />
    </WizardContainer>
  );
};
